/**
 * src/components/spatial/eventCountPolling.ts
 *
 * Which events are worth asking about, and when to stop asking.
 *
 * The count seam already existed: presence produces a number,
 * `setEventCounts` holds it, `deriveEventPresentation` stamps it on the
 * feature, the paint expression reads it. What was missing was anything
 * that fetched. This is the decision part of that, kept separate from
 * the hook so it can be reasoned about without a renderer.
 *
 * Two decisions matter.
 *
 * WHICH EVENTS. Only the ones currently happening or about to. The
 * count is of people present, so an event next month has nothing to
 * report and asking about it spends a request to be told zero. This
 * also means the polling stops on its own: a map with no live events
 * asks nothing, which is the behaviour wanted from "stop updates when
 * the surface is no longer active" without a second switch to forget
 * to turn off.
 *
 * HOW OFTEN. Slowly. An attendance figure on a map is ambient
 * information — nobody is watching a number tick. Thirty seconds
 * matches the layer's own refresh, so the count arrives in step with
 * the features it is painted onto rather than between them.
 *
 * There is no count state in here. The numbers live in exactly one
 * place, which is the map layer's module map, and this file decides
 * only what to ask and whether to ask it.
 */

import type { EventState } from './spatialFeatureDerivation';

/** In step with the spatial layer's own refresh, not faster. */
export const EVENT_COUNT_INTERVAL_MS = 30_000;

/** Never below this, whatever a caller passes. */
export const MIN_EVENT_COUNT_INTERVAL_MS = 10_000;

/**
 * Ids per request.
 *
 * The route caps hydration at 200, and a screen with more than 200
 * live events is a screen where no individual number is legible
 * anyway.
 */
export const MAX_EVENT_IDS = 200;

/**
 * The states worth counting.
 *
 * `recently_ended` is excluded: the people have gone, and a lingering
 * figure on a finished event reads as a crowd that is not there. Its
 * attendance is a history question, answered elsewhere.
 */
const COUNTABLE_STATES: ReadonlySet<string> = new Set<EventState>(['live', 'soon']);

type FeatureLike = {
  properties?: Record<string, unknown> | null;
};

function _id(properties: Record<string, unknown>): string | null {
  const raw = properties.objectId ?? properties.spatialObjectId ?? properties.id;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/**
 * The events on screen worth asking about.
 *
 * Reads the derived `eventState` already stamped on the feature rather
 * than re-deriving it from metadata. Two readings of the same clock is
 * how a map ends up painting one answer and counting another.
 *
 * Deduplicated, because one event can be drawn as several features —
 * a footprint and its floors — and asking three times about one
 * concert is three times the request for the same number.
 */
export function countableEventIds(features: readonly FeatureLike[] | null | undefined): string[] {
  if (!Array.isArray(features)) return [];

  const ids = new Set<string>();
  for (const feature of features) {
    const properties = feature?.properties;
    if (!properties) continue;
    const state = properties.eventState;
    if (typeof state !== 'string' || !COUNTABLE_STATES.has(state)) continue;
    const id = _id(properties);
    if (id) ids.add(id);
    if (ids.size >= MAX_EVENT_IDS) break;
  }
  return Array.from(ids);
}

/**
 * Whether to ask at all.
 *
 * Four ways to be told no, and each is a real situation: the layer is
 * switched off, the tab is in the background, nothing on screen is
 * happening, or the ids have not settled yet. An empty list is the
 * common one and the reason this does not need its own switch.
 */
export function shouldPollEventCounts(input: {
  enabled: boolean;
  documentHidden?: boolean;
  eventIds: readonly string[] | null | undefined;
}): boolean {
  if (!input.enabled) return false;
  if (input.documentHidden) return false;
  return Array.isArray(input.eventIds) && input.eventIds.length > 0;
}

/** Clamped, so a caller cannot turn an ambient number into a poll storm. */
export function eventCountIntervalMs(requested?: number | null): number {
  if (!Number.isFinite(requested as number)) return EVENT_COUNT_INTERVAL_MS;
  return Math.max(MIN_EVENT_COUNT_INTERVAL_MS, requested as number);
}

/**
 * Whether a fresh answer changes anything worth repainting.
 *
 * Normalizing a whole feature collection to restamp identical numbers
 * is work the map can see. Compared by content rather than by
 * reference because the answer arrives as a new object every time.
 */
export function countsChanged(
  previous: Readonly<Record<string, number>> | null | undefined,
  next: Readonly<Record<string, number>> | null | undefined,
): boolean {
  const before = previous || {};
  const after = next || {};
  const beforeKeys = Object.keys(before);
  const afterKeys = Object.keys(after);
  if (beforeKeys.length !== afterKeys.length) return true;
  for (const key of afterKeys) {
    if (before[key] !== after[key]) return true;
  }
  return false;
}

/**
 * Keep only what a count may be.
 *
 * The same guard `setEventCounts` applies, done here as well so a
 * malformed answer is discarded before it is compared — otherwise a
 * string arriving where a number belongs reads as a change on every
 * poll and repaints forever.
 */
export function sanitizeCounts(
  raw: Record<string, unknown> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [id, value] of Object.entries(raw)) {
    if (typeof id === 'string' && id && Number.isInteger(value) && (value as number) >= 0) {
      out[id] = value as number;
    }
  }
  return out;
}
